import { Component, OnInit, AnimationCallbackEvent, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { gsap } from 'gsap';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Select } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { TabsModule } from 'primeng/tabs';

import { NewTriggerDialogComponent, NewTriggerFormValue } from './new-trigger-dialog/new-trigger-dialog';
import { EmailEditorDrawerComponent } from './email-editor-drawer/email-editor-drawer';
import { TriggerFiltersComponent, TriggerFilterState } from './triggerfilters/triggerfilters';
import { TriggerCardComponent } from './trigger-card/trigger-card';
import { EditTriggerDialogComponent } from './edit-trigger-dialog/edit-trigger-dialog';
import { PlaceholdersDrawerComponent } from './placeholders-drawer/placeholders-drawer';
import { ITrigger } from '../../../../../../../../../common/common-interfaces/trigger';
import { CustomEmailKind, ICustomEmailState } from '@common-interfaces/custom-email';
import { BookingEmailsComponent } from './booking-emails/booking-emails';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { IClient } from '../../../../../../../../../common/common-interfaces/client';
import { IPackage } from '../../../../../../../../../common/common-interfaces/package';
import { IBeefreeTemplateEditorConfig } from '../../shared/third-party/beefree/beefree-template-editor.component';
import { Utils } from '../../../../services/utils.service';
import { Api } from '../../../../services/api.service';
import { AuthService } from '../../../../services/auth.service';
import { MailluneService } from '../../../../services/maillune.service';
import {
    IMailluneTemplateEditorConfig,
    IMailluneTemplateSaved
} from '../../shared/third-party/maillune/maillune-template-editor.component';
import { debugLogger } from '@common-helpers/debug-logger';
import { translatedOptions } from './translated-options';

@Component({
    selector: 'triggers-list',
    templateUrl: './triggers.component.html',
    styleUrls: ['./triggers.component.scss'],
    imports: [
        FormsModule,
        TranslatePipe,
        Select,
        Skeleton,
        TriggerFiltersComponent,
        TriggerCardComponent,
        EditTriggerDialogComponent,
        PlaceholdersDrawerComponent,
        TabsModule,
        EmailEditorDrawerComponent,
        NewTriggerDialogComponent,
        BookingEmailsComponent,
        PageHeaderComponent
    ]
})
export class TriggersComponent implements OnInit {
    message = inject(MessageService);
    translate = inject(TranslateService);
    api = inject(Api);
    utility = inject(Utils);
    auth = inject(AuthService);
    maillune = inject(MailluneService);
    confirm = inject(ConfirmationService);

    // ── Signal-based view children ──────────────────────────────────────────
    newTriggerDialogRef = viewChild<NewTriggerDialogComponent>('newTriggerDialogRef');
    emailEditorDrawerRef = viewChild<EmailEditorDrawerComponent>('emailEditorDrawerRef');

    // ── State ────────────────────────────────────────────────────────────────
    clients = signal<IClient[]>([]);
    triggers = signal<ITrigger[]>([]);
    originalTriggers = signal<ITrigger[]>([]);
    selectedClient = signal<IClient | null>(null);
    loading = signal<boolean>(true);
    creatingTrigger = signal<boolean>(false);
    availablePackages = signal<IPackage[]>([]);
    emailEditorVisible = signal<boolean>(false);
    placeholdersVisible = signal<boolean>(false);
    // Set when opened from the Maillune editor, whose merge-tag pills already cover the static placeholders.
    placeholdersDynamicOnly = signal<boolean>(false);
    beefreeConfig = signal<IBeefreeTemplateEditorConfig | null>(null);
    currentBeeFreeTemplate = signal<any>(null);
    currentBeeFreeTrigger = signal<ITrigger | null>(null);
    editingTrigger = signal<boolean>(false);
    editingTriggerData = signal<ITrigger | null>(null);

    mailluneConfig = signal<IMailluneTemplateEditorConfig | null>(null);
    currentMailluneEmailData = signal<any>(null);

    // Placeholders offered as Maillune merge-tag pills (replaces the Beefree placeholders drawer).
    readonly mailluneMergeTagPlaceholders = [
        { placeHolder: '{BOOKING_NUMBER}', label: 'BOOKING_NUMBER' },
        { placeHolder: '{FIRST_NAME}', label: 'CUSTOMER_FIRST_NAME' },
        { placeHolder: '{LAST_NAME}', label: 'CUSTOMER_LAST_NAME' },
        { placeHolder: '{BIRTHDAY_PERSON_NAME}', label: 'BIRTHDAY_PERSON_NAME' },
        { placeHolder: '{UNSUBSCRIBE_LINK}', label: 'UNSUBSCRIBE_LINK' }
    ];

    activeTab = signal<'automations' | 'emails'>('automations');

    newTriggerDialogVisible = signal<boolean>(false);

    // ── Built-in booking emails ───────────────────────────────────────────
    defaultEmails = signal<ICustomEmailState[]>([]);
    defaultEmailsLoading = signal<boolean>(false);
    busyDefaultKind = signal<CustomEmailKind | null>(null);
    /** Set while the Maillune editor is open for a built-in email rather than a trigger. */
    editingDefaultEmail = signal<ICustomEmailState | null>(null);

    suggestedTriggers = signal<{ birthdayEmail?: boolean; birthdaySms?: boolean }>({});

    searchString = signal<string>('');
    selectedTriggerTypes = signal<string[]>([]);
    selectedActionTypes = signal<string[]>([]);
    filterEnabled = signal<string>('ALL');

    readonly triggerTypeOptions = translatedOptions([
        { label: 'TRIGGER_BEFORE_BOOKING_START', value: 'BEFORE_BOOKING_START' },
        { label: 'TRIGGER_BOOKING_CREATED', value: 'BOOKING_CREATED' },
        { label: 'TRIGGER_BOOKING_CHANGED', value: 'BOOKING_CHANGED' },
        { label: 'TRIGGER_BOOKING_CANCELLED', value: 'BOOKING_CANCELLED' },
        { label: 'TRIGGER_BOOKING_CHECKED_IN', value: 'BOOKING_CHECKED_IN' }
    ]);

    readonly actionTypeOptions = translatedOptions([
        { label: 'TRIGGER_ACTION_SEND_EMAIL', value: 'SEND_EMAIL' },
        { label: 'TRIGGER_ACTION_SCHEDULE_EMAIL', value: 'SCHEDULE_EMAIL' },
        { label: 'TRIGGER_ACTION_SEND_SMS', value: 'SEND_SMS' },
        { label: 'TRIGGER_ACTION_SCHEDULE_SMS', value: 'SCHEDULE_SMS' }
    ]);

    readonly enabledFilterOptions = translatedOptions([
        { label: 'ALL', value: 'ALL' },
        { label: 'ENABLED', value: 'ENABLED' },
        { label: 'DISABLED', value: 'DISABLED' }
    ]);

    async ngOnInit(): Promise<void> {
        const clientsResp = await this.api.currentorganization().get<IClient[]>(`/clients/available`);
        if (clientsResp.data) {
            this.clients.set(clientsResp.data);
            this.selectedClient.set(this.clients()[0] ?? null);
            await this.selectedClientChanged();
        }

        this.loading.set(false);
    }

    // ── Filter handling ────────────────────────────────────────────────────

    onFiltersChanged(state: TriggerFilterState): void {
        this.searchString.set(state.searchString);
        this.selectedTriggerTypes.set(state.selectedTriggerTypes);
        this.selectedActionTypes.set(state.selectedActionTypes);
        this.filterEnabled.set(state.filterEnabled);
        this.searchTriggers();
    }

    searchTriggers(): void {
        this.triggers.set(
            this.originalTriggers().filter((trigger) => {
                const nameMatch = !this.searchString() || trigger.name?.toLowerCase().includes(this.searchString().toLowerCase());

                const triggerTypeMatch =
                    !this.selectedTriggerTypes().length || this.selectedTriggerTypes().includes(trigger.triggerData.name);

                const actionTypeMatch = !this.selectedActionTypes().length || this.selectedActionTypes().includes(trigger.actionData.name!);

                const enabledMatch =
                    this.filterEnabled() === 'ALL' ||
                    (this.filterEnabled() === 'ENABLED' && trigger.enabled) ||
                    (this.filterEnabled() === 'DISABLED' && !trigger.enabled);

                return nameMatch && triggerTypeMatch && actionTypeMatch && enabledMatch;
            })
        );
    }

    // ── Client ─────────────────────────────────────────────────────────────

    onSelectedClientChange(client: IClient | null): void {
        this.selectedClient.set(client);
        this.selectedClientChanged();
    }

    async selectedClientChanged(): Promise<void> {
        await Promise.all([this.fetchTriggers(), this.fetchDefaultEmails()]);
        await this.fetchAvailablePackages();
        await this.updateSuggestedTriggers();
    }

    async fetchTriggers(): Promise<void> {
        const targetClient = this.selectedClient()?.id;

        if (!targetClient) {
            return;
        }
        this.loading.set(true);
        const clientTriggers = await this.api.currentOrganizationClient(targetClient).get<ITrigger[]>(`/triggers`);

        if (!clientTriggers.success) {
            this.loading.set(false);
            return;
        }
        // Initialize all data in memory first
        for (const trigger of clientTriggers.data) {
            if (trigger.triggerData) {
                await this.ensureTriggerDataInitialized(trigger);
            }
        }
        // Set the signals once
        this.originalTriggers.set(clientTriggers.data);
        this.triggers.set([...clientTriggers.data]);

        this.loading.set(false);
    }

    async fetchAvailablePackages(): Promise<void> {
        const targetClient = this.selectedClient()?.id;

        if (!targetClient) {
            return;
        }
        const packages = await this.api.client(targetClient).get<IPackage[]>(`/packages`);
        this.availablePackages.set((packages.data ?? []).filter((p) => !p.disabled));
    }

    updateTrigger(index: number, updated: ITrigger): void {
        this.triggers.update((list) => {
            const copy = [...list];
            copy[index] = updated;
            return copy;
        });
    }

    // ── New trigger dialog ─────────────────────────────────────────────────

    openNewTriggerDialog(): void {
        this.newTriggerDialogRef()?.reset();
        this.newTriggerDialogVisible.set(true);
    }

    async confirmNewTrigger(form: NewTriggerFormValue): Promise<void> {
        const targetClient = this.selectedClient()?.id;

        if (!targetClient) {
            return;
        }
        this.creatingTrigger.set(true);
        try {
            const newTrigger: ITrigger = {
                name: form.name,
                triggerData: { name: form.triggerType as any },
                actionData: { name: form.actionType as any },
                enabled: true
            };
            const result = await this.api.currentOrganizationClient(targetClient).put<any>(`/triggers`, newTrigger);

            if (result.success) {
                await this.ensureTriggerDataInitialized(result.data);
                this.triggers.update((t) => [...t, result.data]);
                this.originalTriggers.set(this.triggers());
                this.newTriggerDialogVisible.set(false);
            }
        } catch (error) {
            debugLogger.error('Failed to create trigger:', error);
        } finally {
            this.creatingTrigger.set(false);
        }
    }

    openEditTriggerDialog(trigger: ITrigger): void {
        this.editingTriggerData.set(trigger);
        this.editingTrigger.set(true);
    }

    closeEditTriggerDialog(trigger: any): void {
        this.triggerChanged(trigger);
        this.editingTrigger.set(false);
        this.editingTriggerData.set(null);
    }

    abortEditTriggerDialog(): void {
        this.editingTrigger.set(false);
        this.editingTriggerData.set(null);
    }

    // ── Trigger CRUD ───────────────────────────────────────────────────────

    async triggerChanged(trigger: ITrigger): Promise<void> {
        const targetClient = this.selectedClient()?.id;

        if (!targetClient) {
            return;
        }
        await this.ensureTriggerDataInitialized(trigger);

        this.originalTriggers.update((t) => t.map((triggy) => (triggy.id === trigger.id ? { ...trigger } : triggy)));
        this.searchTriggers();

        try {
            await this.api.currentOrganizationClient(targetClient).patch<ITrigger[]>(`/triggers/${trigger.id}`, trigger);
            this.message.add({ severity: 'success', summary: this.translate.instant('TRIGGER_SAVED') });
        } catch (_error) {
            // Rollback on failure
            this.originalTriggers.update((t) => t.map((triggy) => (triggy.id === trigger.id ? trigger : triggy)));
            this.searchTriggers();
            this.message.add({ severity: 'error', summary: this.translate.instant('TRIGGER_SAVE_FAILED') });
        }
    }

    private async newTrigger(): Promise<ITrigger | null> {
        const targetClient = this.selectedClient()?.id;

        if (!targetClient) {
            return null;
        }
        this.creatingTrigger.set(true);
        try {
            const result = await this.api.currentOrganizationClient(targetClient).put<any>(`/triggers`, {
                name: '',
                triggerData: { name: 'BOOKING_CREATED' },
                actionData: {},
                enabled: true
            });
            if (result.success) {
                this.triggers.update((t) => [...t, result.data]);
                return result.data as ITrigger;
            }
            return null;
        } finally {
            this.creatingTrigger.set(false);
        }
    }

    // ── Email editor ───────────────────────────────────────────────────────

    get mailluneEnabled(): boolean {
        return this.maillune.enabled;
    }

    async openEmailEditor(trigger: ITrigger, beeFreeTemplate: any): Promise<void> {
        const targetClient = this.selectedClient()?.id;

        if (!targetClient) {
            return;
        }
        this.currentBeeFreeTrigger.set(trigger);

        // Full replacement: when the org uses Maillune, Beefree is never touched.
        if (this.mailluneEnabled) {
            await this.openMailluneEmailEditor(trigger);
            return;
        }

        this.emailEditorVisible.set(true);
        this.currentBeeFreeTemplate.set(beeFreeTemplate);

        if (!this.currentBeeFreeTemplate()?.templateJson) {
            const resp = await this.api.currentOrganizationClient(targetClient).get<any>(`/triggers/default-email-template`);
            if (resp.success) {
                this.currentBeeFreeTemplate.update((tpl) => ({ ...tpl, templateJson: resp.data }));
            }
        }

        await this.initBeefree();
    }

    private mailluneEmailData(trigger: ITrigger): any {
        if (trigger.actionData.name === 'SEND_EMAIL') return trigger.actionData.sendEmailData ?? null;
        if (trigger.actionData.name === 'SCHEDULE_EMAIL') return trigger.actionData.scheduleEmailData ?? null;
        return null;
    }

    private async openMailluneEmailEditor(trigger: ITrigger): Promise<void> {
        const targetClient = this.selectedClient()?.id;
        const emailData = this.mailluneEmailData(trigger);
        if (!targetClient || !emailData) return;
        this.editingDefaultEmail.set(null);

        const token = await this.maillune.getSessionToken();
        if (!token) {
            this.message.add({ severity: 'error', summary: this.translate.instant('EMAIL_EDITOR_UNAVAILABLE') });
            return;
        }

        emailData.thirdParty.maillune ??= {};
        this.currentMailluneEmailData.set(emailData);

        // Seed the editor with the existing Beefree design (or the default template) so Maillune
        // can convert and open it; a native Maillune design takes precedence once one exists.
        let beefreeSeed = emailData.thirdParty.beefree?.templateJson;
        if (!emailData.thirdParty.maillune.templateJson && !beefreeSeed) {
            const resp = await this.api.currentOrganizationClient(targetClient).get<any>(`/triggers/default-email-template`);
            if (resp.success) {
                beefreeSeed = resp.data;
            }
        }

        this.mailluneConfig.set({
            token,
            userId: `ORG-${this.auth.organizationId()}`,
            templateJson: emailData.thirdParty.maillune.templateJson,
            beefreeTemplate: beefreeSeed,
            locale: this.maillune.locale,
            theme: this.maillune.theme,
            accentColor: this.maillune.accentColor,
            mergeTags: this.maillune.buildMergeTags(this.mailluneMergeTagPlaceholders)
        });
        this.emailEditorVisible.set(true);
    }

    async closeEmailEditor(): Promise<void> {
        this.emailEditorVisible.set(false);
        setTimeout(() => {
            this.currentBeeFreeTemplate.set(null);
            this.currentMailluneEmailData.set(null);
            this.beefreeConfig.set(null);
            this.mailluneConfig.set(null);
            this.editingDefaultEmail.set(null);
        }, 300);
    }

    async initBeefree(): Promise<void> {
        let currentUser = this.auth.currentUser()?.settings;

        if (!currentUser) {
            return;
        }

        let locale = navigator.language || 'en-US';
        if (currentUser.language && currentUser.country) {
            locale = `${currentUser.language}-${currentUser.country.toUpperCase()}`;
        }

        const tokenReq = await this.api.crm().get<any>(`/third-party/beefree/token`);

        if (tokenReq.success) {
            let token = tokenReq.data;
            this.beefreeConfig.set({
                uid: `ORG-${this.auth.organizationId()}`,
                token,
                templateJson: this.currentBeeFreeTemplate()?.templateJson,
                config: { autosave: 30, language: locale, contentDialog: {} }
            });
        }
    }

    async beefreeTemplateSaved(data: { json: string; html: string }): Promise<void> {
        this.currentBeeFreeTemplate.update((tpl) => ({ ...tpl, templateJson: data.json, templateHtml: data.html }));
        if (this.emailEditorVisible()) {
            this.emailEditorVisible.set(false);
        }
        const savedTrigger = this.currentBeeFreeTrigger();
        setTimeout(async () => {
            this.beefreeConfig.set(null);
            if (savedTrigger) {
                await this.triggerChanged(savedTrigger);
            }
        }, 300);
    }

    async emailTemplateSaved(data: IMailluneTemplateSaved | { json: string; html: string }): Promise<void> {
        if (this.editingDefaultEmail()) {
            await this.saveDefaultEmailTemplate(data as IMailluneTemplateSaved);
        } else if (this.mailluneConfig()) {
            await this.mailluneTemplateSaved(data as IMailluneTemplateSaved);
        } else {
            await this.beefreeTemplateSaved(data as { json: string; html: string });
        }
    }

    async mailluneTemplateSaved(data: IMailluneTemplateSaved): Promise<void> {
        const emailData = this.currentMailluneEmailData();
        if (!emailData) return;

        emailData.thirdParty.maillune ??= {};
        emailData.thirdParty.maillune.templateJson = data.json;
        emailData.thirdParty.maillune.templateHtml = data.html;
        if (data.thumbnail) emailData.thirdParty.maillune.thumbnailImageData = data.thumbnail;

        if (this.emailEditorVisible()) {
            this.emailEditorVisible.set(false);
        }
        const savedTrigger = this.currentBeeFreeTrigger();
        setTimeout(async () => {
            this.mailluneConfig.set(null);
            this.currentMailluneEmailData.set(null);
            if (savedTrigger) {
                await this.triggerChanged(savedTrigger);
            }
        }, 300);
    }

    // ── Built-in booking emails ───────────────────────────────────────────

    async fetchDefaultEmails(): Promise<void> {
        const targetClient = this.selectedClient()?.id;
        if (!targetClient) {
            return;
        }
        this.defaultEmailsLoading.set(true);
        try {
            const res = await this.api.currentOrganizationClient(targetClient).get<ICustomEmailState[]>(`/custom-emails`);
            if (res.success) {
                this.defaultEmails.set(res.data);
            }
        } catch (error) {
            debugLogger.error('Failed to load booking emails', error);
            this.defaultEmails.set([]);
        } finally {
            this.defaultEmailsLoading.set(false);
        }
    }

    /** Switch to the custom template; the editor opens first when there is no template yet. */
    async useCustomEmail(email: ICustomEmailState): Promise<void> {
        if (email.template?.templateHtml) {
            await this.saveDefaultEmail(email.kind, {}, 'BOOKING_EMAIL_CUSTOM_ON');
            return;
        }
        await this.openDefaultEmailEditor(email);
    }

    useDefaultEmail(email: ICustomEmailState): void {
        this.confirm.confirm({
            header: this.translate.instant('BOOKING_EMAIL_RESTORE_TITLE'),
            message: this.translate.instant('BOOKING_EMAIL_RESTORE_TEXT'),
            icon: 'ph ph-arrow-counter-clockwise',
            acceptLabel: this.translate.instant('BOOKING_EMAIL_MODE_DEFAULT'),
            rejectLabel: this.translate.instant('CANCEL'),
            rejectButtonStyleClass: 'p-button-text',
            accept: () => this.restoreDefaultEmail(email.kind)
        });
    }

    async changeDefaultEmailSubject(change: { email: ICustomEmailState; subject: string }): Promise<void> {
        await this.saveDefaultEmail(change.email.kind, { subject: change.subject }, 'BOOKING_EMAIL_SAVED');
    }

    async openDefaultEmailEditor(email: ICustomEmailState): Promise<void> {
        const targetClient = this.selectedClient()?.id;
        if (!targetClient) {
            return;
        }

        const token = await this.maillune.getSessionToken();
        if (!token) {
            this.message.add({ severity: 'error', summary: this.translate.instant('EMAIL_EDITOR_UNAVAILABLE') });
            return;
        }

        // A saved design wins; otherwise start from the venue's standard email.
        const design = email.template?.templateJson ?? email.starterTemplate;

        this.currentBeeFreeTrigger.set(null);
        this.currentMailluneEmailData.set(null);
        this.editingDefaultEmail.set(email);
        this.mailluneConfig.set({
            token,
            userId: `ORG-${this.auth.organizationId()}`,
            templateJson: design,
            locale: this.maillune.locale,
            theme: this.maillune.theme,
            accentColor: this.maillune.accentColor,
            mergeTags: this.maillune.buildMergeTags(email.placeholders.map((placeholder) => this.placeholderTag(placeholder)))
        });
        this.emailEditorVisible.set(true);
    }

    private async saveDefaultEmailTemplate(data: IMailluneTemplateSaved): Promise<void> {
        const email = this.editingDefaultEmail();
        if (!email) {
            return;
        }
        this.emailEditorVisible.set(false);
        await this.saveDefaultEmail(
            email.kind,
            {
                // The first save starts from the venue's current built-in subject.
                ...(email.template ? {} : { subject: email.defaultSubject }),
                templateJson: data.json,
                templateHtml: data.html,
                ...(data.thumbnail ? { thumbnailImageData: data.thumbnail } : {})
            },
            email.mode === 'CUSTOM' ? 'BOOKING_EMAIL_SAVED' : 'BOOKING_EMAIL_CUSTOM_ON'
        );
        setTimeout(() => {
            this.mailluneConfig.set(null);
            this.editingDefaultEmail.set(null);
        }, 300);
    }

    /** A merge-tag pill: translated name when there is one, otherwise the placeholder's own name. */
    private placeholderTag(placeholder: string): { placeHolder: string; label: string } {
        const name = placeholder.replace(/[{}]/g, '');
        const key = `PH_${name.replace(/\./g, '_')}`;
        const label = this.translate.instant(key);
        return { placeHolder: placeholder, label: label === key ? name : label };
    }

    private async saveDefaultEmail(kind: CustomEmailKind, body: object, successKey: string): Promise<void> {
        const targetClient = this.selectedClient()?.id;
        if (!targetClient) {
            return;
        }
        this.busyDefaultKind.set(kind);
        try {
            await this.api.currentOrganizationClient(targetClient).put(`/custom-emails/${kind}`, body);
            this.message.add({ severity: 'success', summary: this.translate.instant(successKey) });
        } catch (error) {
            debugLogger.error('Failed to save booking email', error);
            this.message.add({ severity: 'error', summary: this.translate.instant('BOOKING_EMAIL_SAVE_FAILED') });
        } finally {
            this.busyDefaultKind.set(null);
            await this.fetchDefaultEmails();
        }
    }

    private async restoreDefaultEmail(kind: CustomEmailKind): Promise<void> {
        const targetClient = this.selectedClient()?.id;
        if (!targetClient) {
            return;
        }
        this.busyDefaultKind.set(kind);
        try {
            await this.api.currentOrganizationClient(targetClient).post(`/custom-emails/${kind}/restore`, {});
            this.message.add({ severity: 'success', summary: this.translate.instant('BOOKING_EMAIL_DEFAULT_ON') });
        } catch (error) {
            debugLogger.error('Failed to restore booking email', error);
            this.message.add({ severity: 'error', summary: this.translate.instant('BOOKING_EMAIL_SAVE_FAILED') });
        } finally {
            this.busyDefaultKind.set(null);
            await this.fetchDefaultEmails();
        }
    }

    // ── Placeholders ───────────────────────────────────────────────────────

    async showAvailablePlaceholders(dynamicOnly = false): Promise<void> {
        this.placeholdersDynamicOnly.set(dynamicOnly);
        this.placeholdersVisible.set(true);
    }

    // ── Suggested triggers ─────────────────────────────────────────────────

    async updateSuggestedTriggers(): Promise<void> {
        const hasBirthdayPackage = this.availablePackages().some((p) => p.type === 'BIRTHDAY');

        let birthdayEmail = hasBirthdayPackage;
        let birthdaySms = hasBirthdayPackage;

        for (const trigger of this.triggers()) {
            if (trigger.metadata?.suggestedBirthdayEmailTrigger) {
                birthdayEmail = false;
            }
            if (trigger.metadata?.suggestedBirthdaySmsTrigger) {
                birthdaySms = false;
            }
        }

        this.suggestedTriggers.set({
            birthdayEmail: birthdayEmail || undefined,
            birthdaySms: birthdaySms || undefined
        });
    }

    async createSuggestedBirthdayEmailTrigger(): Promise<void> {
        const newTrigger = await this.newTrigger();
        if (!newTrigger) {
            return;
        }

        newTrigger.name = 'Happy birthday email';
        newTrigger.triggerData.name = 'BOOKING_CHECKED_IN';
        newTrigger.triggerData.bookingCheckedInData = {
            specificPackageIds: this.availablePackages()
                .filter((p) => p.type === 'BIRTHDAY')
                .map((p) => p.id) as string[]
        };
        newTrigger.actionData.name = 'SCHEDULE_EMAIL';
        newTrigger.actionData.scheduleEmailData = {
            thirdParty: { beefree: { uid: `ORG-${this.auth.organizationId()}` } },
            subject: 'Happy Birthday!',
            senderName: this.auth.currentOrganization()?.organizationName,
            receivers: ['CUSTOMER'],
            timeValue: 11,
            timeUnit: 'months'
        };
        newTrigger.metadata = { suggestedBirthdayEmailTrigger: true };

        const targetClient = this.selectedClient()?.id;
        if (!targetClient) {
            return;
        }
        const template = await this.api.currentOrganizationClient(targetClient).get<any>(`/triggers/default-birthday-email-template`);

        if (template.success && newTrigger.actionData?.scheduleEmailData?.thirdParty?.beefree) {
            newTrigger.actionData.scheduleEmailData.thirdParty.beefree.templateJson = template.data;
            await this.triggerChanged(newTrigger);
            await this.updateSuggestedTriggers();
            await this.openEmailEditor(newTrigger, newTrigger.actionData.scheduleEmailData.thirdParty.beefree);
        }
    }

    async createSuggestedBirthdaySmsTrigger(): Promise<void> {
        const newTrigger = await this.newTrigger();
        const packages = await this.availablePackages();
        if (!newTrigger && !packages) {
            return;
        }

        newTrigger!.name = 'Happy birthday SMS';
        newTrigger!.triggerData.name = 'BOOKING_CHECKED_IN';
        newTrigger!.triggerData.bookingCheckedInData = {
            specificPackageIds: packages.filter((p) => p.type === 'BIRTHDAY').map((p) => p.id) as string[]
        };
        newTrigger!.actionData.name = 'SCHEDULE_SMS';
        newTrigger!.actionData.scheduleSmsData = {
            message: 'Happy birthday!\n\n({PROMO_CODE:{"uses":1,"validDays":90,"percent":10}})',
            receivers: ['CUSTOMER'],
            timeValue: 11,
            timeUnit: 'months'
        };
        newTrigger!.metadata = { suggestedBirthdaySmsTrigger: true };

        await this.triggerChanged(newTrigger!);
        await this.updateSuggestedTriggers();
    }

    // ── Data initialisation ────────────────────────────────────────────────

    async ensureTriggerDataInitialized(trigger: ITrigger): Promise<void> {
        switch (trigger.triggerData.name) {
            case 'BEFORE_BOOKING_START':
                trigger.triggerData.beforeBookingData ??= { specificPackageIds: [], timeUnit: 'hours', timeValue: 1 };
                break;
            case 'BOOKING_CREATED':
                trigger.triggerData.bookingCreatedData ??= { specificPackageIds: [] };
                break;
            case 'BOOKING_CHANGED':
                trigger.triggerData.bookingChangedData ??= { specificPackageIds: [] };
                break;
            case 'BOOKING_CANCELLED':
                trigger.triggerData.bookingCancelledData ??= { specificPackageIds: [] };
                break;
            case 'BOOKING_CHECKED_IN':
                trigger.triggerData.bookingCheckedInData ??= { specificPackageIds: [] };
                break;
            default:
                trigger.triggerData.beforeBookingData ??= { specificPackageIds: [], timeUnit: 'hours', timeValue: 1 };
                break;
        }

        const newBeefreeData = (): { uid: string } => ({ uid: `ORG-${this.auth.organizationId()}` });

        switch (trigger.actionData.name) {
            case 'SEND_EMAIL':
                trigger.actionData.sendEmailData ??= {
                    thirdParty: { beefree: newBeefreeData() },
                    receivers: ['CUSTOMER']
                };
                trigger.actionData.sendEmailData.receivers ??= [];
                break;
            case 'SCHEDULE_EMAIL':
                trigger.actionData.scheduleEmailData ??= {
                    thirdParty: { beefree: newBeefreeData() },
                    receivers: ['CUSTOMER'],
                    timeValue: 1,
                    timeUnit: 'hours'
                };
                trigger.actionData.scheduleEmailData.receivers ??= [];
                break;
            case 'SEND_SMS':
                trigger.actionData.sendSmsData ??= { message: '', receivers: ['CUSTOMER'] };
                trigger.actionData.sendSmsData.receivers ??= [];
                break;
            case 'SCHEDULE_SMS':
                trigger.actionData.scheduleSmsData ??= {
                    message: '',
                    receivers: ['CUSTOMER'],
                    timeValue: 1,
                    timeUnit: 'hours'
                };
                trigger.actionData.scheduleSmsData.receivers ??= [];
                break;
        }
    }

    animateStepIn(event: AnimationCallbackEvent): void {
        const cards = event.target.querySelectorAll('p-card');

        if (cards.length) {
            gsap.fromTo(
                cards,
                {
                    opacity: 0,
                    y: 10
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    ease: 'power2.inOut',
                    stagger: 0.03
                }
            );
        } else {
            gsap.fromTo(
                event.target.children,
                {
                    opacity: 0,
                    y: 10
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    ease: 'power1.inOut',
                    stagger: 0.08
                }
            );
        }
    }
}
